import { useMemo, useState } from 'react';

import { Game } from '~/types/game';
import { VoteContext } from '~/services/freePlay/types';
import { FreePlayIdleScreen, GameSelector, GamepadActionSheet, VoteBanner, VoteOverlay } from '~/components/freePlay';

import styles from './DebugFreePlay.module.css';

const mockGames: Game[] = [
    { id: 'bomber-kitten', name: 'Bomber Kitten' },
    { id: 'petit-bac', name: 'Petit Bac' },
    { id: 'unblind-test', name: 'Unblind Test' },
    { id: 'geoloc', name: 'Geoloc' },
    { id: 'gloofy-pop', name: 'Gloofy Pop' },
    { id: 'unquiz', name: 'Unquizz' },
    { id: 'polyvroom', name: 'Polyvroom' },
    { id: 'unport', name: 'Unport' },
    { id: 'eggsit', name: 'Eggsit' },
    { id: 'robrawl', name: 'Robrawl' },
].map((stub) =>
    Object.assign(new Game(), {
        _id: stub.id,
        _name: stub.name,
        _icon: `https://developers.unboared.com/public-assets/images/games/${stub.id}/Icon.png`,
        _cover: `https://developers.unboared.com/public-assets/images/games/${stub.id}/Cover.png`,
        _numberOfPlayers: '2-10',
        _playTime: '8 min',
    })
);

const mockGameIDs = mockGames.map((g) => g.id);

type View =
    | 'sheet'
    | 'start'
    | 'replace'
    | 'vote'
    | 'vote-voted'
    | 'vote-spectator'
    | 'vote-banner'
    | 'idle'
    | 'idle-zh';

export default function DebugFreePlay() {
    const [view, setView] = useState<View>('sheet');
    const [lastPick, setLastPick] = useState<string | null>(null);

    /* Mock vote context: Karim (device-3) proposes Petit Bac, vote ends in 8s.
       __debug__ is the local user (a regular voter for normal views). */
    const mockVote: VoteContext = useMemo(() => {
        const now = Date.now();
        return {
            initiatorID: 'device-3',
            proposedGameID: 'petit-bac',
            eligibleVoters: ['device-1', 'device-2', 'device-3', '__debug__'],
            startedAt: now,
            deadline: now + 8000,
            votes: { 'device-3': 'yes' },
        };
    }, [view]); // re-roll deadline when switching tabs
    const proposedGame = mockGames.find((g) => g.id === 'petit-bac');

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <h1 className={styles.title}>Free-play debug</h1>
                <p className={styles.note}>Composants en isolation · données mockées · aucune action réelle</p>
                <div className={styles.tabs}>
                    {(
                        [
                            { id: 'sheet', label: 'Action sheet' },
                            { id: 'start', label: 'Selector start' },
                            { id: 'replace', label: 'Selector replace' },
                            { id: 'vote', label: 'Vote (à voter)' },
                            { id: 'vote-voted', label: 'Vote (voté)' },
                            { id: 'vote-spectator', label: 'Vote (spectateur)' },
                            { id: 'vote-banner', label: 'Vote — TV banner' },
                            { id: 'idle', label: 'Accueil TV (FR/EN)' },
                            { id: 'idle-zh', label: 'Accueil TV (中文/EN)' },
                        ] as { id: View; label: string }[]
                    ).map(({ id, label }) => (
                        <button
                            key={id}
                            type="button"
                            className={view === id ? styles.tabActive : styles.tab}
                            onClick={() => {
                                setView(id);
                                setLastPick(null);
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                {lastPick && <div className={styles.toast}>Pick: {lastPick}</div>}
            </header>
            <div className={styles.spacer} />

            {view === 'sheet' && (
                <GamepadActionSheet
                    showChangeGame
                    onChangeGame={() => setLastPick('action: changer de jeu')}
                    onProfile={() => setLastPick('action: mon profil')}
                    onClose={() => setLastPick('action: fermer')}
                />
            )}

            {view === 'start' && (
                <GameSelector
                    mode="start"
                    gameIDs={mockGameIDs}
                    games={mockGames}
                    onPick={(id) => setLastPick(`start → ${id}`)}
                />
            )}

            {view === 'replace' && (
                <GameSelector
                    mode="replace"
                    currentGameID="bomber-kitten"
                    gameIDs={mockGameIDs}
                    games={mockGames}
                    eligibleCount={3}
                    onPick={(id) => setLastPick(`replace → ${id}`)}
                    onClose={() => setLastPick('action: fermer')}
                />
            )}

            {/* Attract screen as shown on the TV. Two tabs = the two language pairs
                in production: the FR/EN default, and the per-screen override set via
                `screenInfos.{id}.idleLanguages` (Marseille's Africa/Asia transfer gate).
                No session here → the QR renders empty, everything else is faithful. */}
            {view === 'idle' && (
                <div className={styles.frame}>
                    <FreePlayIdleScreen gameIDs={mockGameIDs} />
                </div>
            )}

            {view === 'idle-zh' && (
                <div className={styles.frame}>
                    <FreePlayIdleScreen gameIDs={mockGameIDs} languages={['zh', 'en']} />
                </div>
            )}

            {view === 'vote' && (
                <div className={styles.frame}>
                    <VoteOverlay
                        vote={mockVote}
                        proposedGame={proposedGame}
                        onVote={(c) => setLastPick(`vote: ${c}`)}
                    />
                </div>
            )}

            {view === 'vote-voted' && (
                <div className={styles.frame}>
                    <VoteOverlay
                        vote={{ ...mockVote, votes: { ...mockVote.votes, __debug__: 'yes' } }}
                        proposedGame={proposedGame}
                        onVote={(c) => setLastPick(`vote: ${c}`)}
                    />
                </div>
            )}

            {view === 'vote-spectator' && (
                <div className={styles.frame}>
                    {/* eligibleVoters does NOT include __debug__ → spectator path */}
                    <VoteOverlay
                        vote={{ ...mockVote, eligibleVoters: ['device-1', 'device-2', 'device-3'] }}
                        proposedGame={proposedGame}
                        onVote={() => {}}
                    />
                </div>
            )}

            {view === 'vote-banner' && (
                <div style={{ minHeight: '60vh', position: 'relative' }}>
                    <p style={{ padding: 'var(--spacing-l)', color: 'var(--color-text-50)' }}>
                        ↓ Le banner s&apos;affiche en bas de l&apos;écran (overlay non bloquant sur la TV).
                    </p>
                    <VoteBanner vote={mockVote} proposedGame={proposedGame} initiatorName="Karim" />
                </div>
            )}
        </div>
    );
}
